import {
  useCharacterCount,
  useWizardFieldMessage,
  WizardFieldFooter,
  wizardFieldCountClasses,
  wizardFieldDisabledShadow,
  wizardFieldFocusRing,
  wizardFieldInvalidRing,
  wizardFieldMessageClasses,
  wizardFieldMessageSlotClasses,
  wizardFieldRestingShadow,
  wizardFieldSuccessRing,
} from "@ui/components/internal/WizardField";
import { resolveValidationState } from "@ui/components/shared/resolveValidationState";
import { tv, type VariantProps } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import { TextField, TextFieldLabel, type TextFieldProps, TextFieldTextArea } from "@ui/primitives/TextField";
import type { ReactNode } from "react";

// The textarea IS the pill (no inner input row), so it can't share the wizard field tv's
// root/field/input split — it builds its own tv from the shared ring + footer constants instead.
export const wizardTextArea = tv({
  slots: {
    root: "group flex w-full flex-col items-start justify-start gap-1.5",
    textarea: [
      "w-full resize-none",
      "text-xl leading-6 font-normal text-neutral-800 placeholder:text-neutral-500",
      "transition duration-100 ease-linear focus:outline-hidden",
    ],
    messageSlot: [wizardFieldMessageSlotClasses, "px-6"],
    message: wizardFieldMessageClasses,
    count: wizardFieldCountClasses,
  },
  variants: {
    variant: {
      default: {
        textarea: ["bg-white scroll-py-6 rounded-3xl p-6", wizardFieldRestingShadow],
      },
      plain: {
        textarea: "min-h-0 overflow-hidden bg-transparent",
      },
    },
    color: {
      red: {},
      green: {},
      purple: {},
      yellow: {},
    },
    // Size the field to its content so it never scrolls at any width — the line count a fixed char
    // limit needs grows as the field narrows. `field-sizing: content` does this natively and OVERRIDES
    // `rows`, so the height floor comes from `min-height`: a sane default ships here, callers extend or
    // override it (e.g. responsively) via `textAreaClassName`. Firefox lacks support (not Baseline):
    // it ignores `field-sizing` and shows the `min-height` box, scrolling internally past the floor.
    autoGrow: {
      true: { textarea: "field-sizing-content min-h-[7.5rem]" },
    },
    isFocused: {
      true: {},
    },
    isDisabled: {
      true: {},
    },
    validationState: {
      invalid: { message: "text-pink-600" },
      success: { message: "text-green-600" },
    },
    // Character count nears its limit → pink, reusing the error hue as a gentle "ease up".
    nearLimit: { true: { count: "text-pink-600" } },
  },
  compoundVariants: [
    // Validation rings reuse the focus ring's 8px+2px geometry (colour-only change) so the ring
    // never resizes on focus → valid/invalid mid-type, which would read as jitter.
    {
      variant: "default",
      validationState: "invalid",
      class: { textarea: wizardFieldInvalidRing },
    },
    {
      variant: "default",
      validationState: "success",
      class: { textarea: wizardFieldSuccessRing },
    },
    // Disabled state (default variant only)
    {
      variant: "default",
      isDisabled: true,
      class: {
        textarea: ["bg-neutral-25 text-neutral-500 cursor-not-allowed", wizardFieldDisabledShadow],
      },
    },

    // Focus ring by color (default variant only)
    {
      variant: "default",
      color: "red",
      isFocused: true,
      isDisabled: false,
      class: { textarea: wizardFieldFocusRing.red },
    },
    {
      variant: "default",
      color: "green",
      isFocused: true,
      isDisabled: false,
      class: { textarea: wizardFieldFocusRing.green },
    },
    {
      variant: "default",
      color: "purple",
      isFocused: true,
      isDisabled: false,
      class: { textarea: wizardFieldFocusRing.purple },
    },
    {
      variant: "default",
      color: "yellow",
      isFocused: true,
      isDisabled: false,
      class: { textarea: wizardFieldFocusRing.yellow },
    },
  ],
  defaultVariants: {
    variant: "default",
    color: "purple",
    isFocused: false,
    isDisabled: false,
  },
});

type WizardTextAreaVariants = VariantProps<typeof wizardTextArea>;

export interface WizardTextAreaProps extends TextFieldProps {
  label: string;
  isLabelHidden?: boolean;
  color?: WizardTextAreaVariants["color"];
  variant?: WizardTextAreaVariants["variant"];
  placeholder?: string;
  rows?: number;
  /** Size the field to its content instead of a fixed `rows` count. Ships a default `min-h` floor;
   *  extend or override it (e.g. responsively) via `textAreaClassName`. `field-sizing` overrides `rows`;
   *  Firefox falls back to a fixed `min-h` box that scrolls past the floor. */
  autoGrow?: boolean;
  /** Extra classes merged onto the inner `<textarea>` — e.g. a responsive `min-h-*` floor for `autoGrow`. */
  textAreaClassName?: string;
  /** Validation message rendered below the field when `isInvalid`. */
  errorMessage?: ReactNode;
  /** Success message rendered below the field once it has validated clean. Takes a green
   *  ring. Suppressed while `isInvalid`. The pill textarea has no trailing slot, so unlike
   *  StandardTextField it shows no validation icon. */
  successMessage?: ReactNode;
  /** When true and `maxLength` is set, renders a live `used/max` count in the footer (right),
   *  turning pink as it nears the limit. */
  showCharacterCount?: boolean;
}

export function WizardTextArea({
  label,
  isLabelHidden,
  color = "purple",
  variant = "default",
  placeholder,
  rows,
  autoGrow,
  textAreaClassName,
  errorMessage,
  successMessage,
  showCharacterCount,
  ...props
}: WizardTextAreaProps) {
  // Counts by code point (not UTF-16 `.length`) via the shared hook, matching WizardTextField and
  // the server's char-based length validation.
  const count = useCharacterCount({
    value: typeof props.value === "string" ? props.value : "",
    maxLength: props.maxLength,
    isEnabled: showCharacterCount,
  });
  const validationState = resolveValidationState(props.isInvalid, successMessage);
  const { messageId, ariaDescribedBy } = useWizardFieldMessage({
    isInvalid: props.isInvalid,
    errorMessage,
    successMessage,
    ariaDescribedBy: props["aria-describedby"],
  });
  const styles = wizardTextArea({ variant, color, validationState, nearLimit: count.nearLimit });

  return (
    <TextField
      {...props}
      aria-describedby={ariaDescribedBy}
      className={composeRenderProps(props.className, (className) => styles.root({ className }))}
    >
      {() => (
        <>
          <TextFieldLabel className={isLabelHidden ? "sr-only" : undefined}>{label}</TextFieldLabel>
          <TextFieldTextArea
            className={({ isFocused, isDisabled }) =>
              wizardTextArea({
                variant,
                color,
                autoGrow,
                isFocused: isFocused && !validationState,
                isDisabled,
                validationState,
              }).textarea({ className: textAreaClassName })
            }
            placeholder={placeholder}
            rows={rows}
          />
          <WizardFieldFooter
            classNames={{ messageSlot: styles.messageSlot(), message: styles.message(), count: styles.count() }}
            countText={count.isVisible ? count.text : undefined}
            errorMessage={errorMessage}
            isInvalid={props.isInvalid}
            messageId={messageId}
            successMessage={successMessage}
          />
        </>
      )}
    </TextField>
  );
}
